import { Trans, useLingui } from "@lingui/react/macro";
import { FormProvider, useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import Form from "@/components/Form/Form";
import { FilledButton } from "@medoma/components/Button/Button";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import Checkbox from "@/components/Checkbox/Checkbox";
import Checkboxes from "@/components/Checkbox/Checkboxes";

import { DescriptionInput } from "@/forms/AddActivityForm/DescriptionInput";
import { Scheduling } from "@/forms/AddActivityForm/Scheduling/Scheduling";
import { CompetencePicker } from "@/forms/AddActivityForm/CompetencePicker";
import { MeasurementsPicker } from "@/forms/AddActivityForm/MeasurementsPicker";
import { ActivityDuration } from "@/forms/AddActivityForm/ActivityDuration";
import { AddInvoicingCodesAction } from "@/forms/AddActivityForm/AddInvoicingCodesAction";

import { activityKeys, addActivity } from "@/api/Activities";
import { knownFeatureFlagsSchema, useFeatureFlag } from "@/api/FeatureFlags";
import { saveActivityInvoicingCodes, shouldSaveInvoice } from "@/api/Invoicing";
import type { IActivityTemplate } from "@/api/Templates";

import {
  categorySchema,
  getActivityCategoryConfig,
  timeOfDaySchema,
} from "@medoma/models/activities";
import { machineFormat } from "@medoma/models/date-and-time";

import { deducedError } from "@/Utils/ErrorUtils";
import { useTemplatesContext } from "../TemplatesContext";
import { newActivityFromActivityTemplate } from "./newActivityFromActivityTemplate";
import type { CreateActivityFromTemplateFormFields } from "./createActivityFromTemplateFormSchema";
import { organizationUsesInvoicingCodes } from "@medoma/models/organizations";
import { useUserProfile } from "@/api/Users";
import { routeKeys } from "@/api/Routes";
import { shiftKeys } from "@/api/Shifts";

type CreateActivityFromTemplateFormProps = {
  template: IActivityTemplate;
  onSuccess?: () => void;
};

export const CreateActivityFromTemplateForm = ({
  template: { template, templateId, revisionNumber: templateRevision },
  onSuccess,
}: CreateActivityFromTemplateFormProps) => {
  const {
    title,
    category,
    description,
    measurements,
    requiredCompetences,
    procedureCodes,
    productCodes,
    durationOption,
    customDuration,
    customDurationUnit,
    frequency,
    recurrence,
    weekdays,
    customRecurrenceInterval,
    customRecurrenceIntervalUnit,
    timeslots,
    timeSensitivity,
    timeCategory,
    recurrencesPerDay,
    doubleStaffing,
    hidden,
  } = template;

  const { t } = useLingui();
  const queryClient = useQueryClient();

  const { selectedPatientId: patientId, defaultStartDate } =
    useTemplatesContext();

  const { organization } = useUserProfile();

  const { data: showDurationInUI } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.ShowDurationInUI,
  );

  // Map template values to form default values (title and category come from template, not form)
  const methods = useForm<CreateActivityFromTemplateFormFields>({
    defaultValues: {
      description: description ?? "",
      measurements,
      requiredCompetences,
      procedureCodes: procedureCodes ?? [],
      productCodes: productCodes ?? [],
      durationOption,
      customDuration,
      customDurationUnit,
      frequency,
      recurrence,
      weekdays,
      customRecurrenceInterval,
      customRecurrenceIntervalUnit,
      timeslots,
      timeSensitivity,
      timeCategory,
      recurrencesPerDay:
        recurrencesPerDay ??
        (timeCategory === timeOfDaySchema.enum.Specific
          ? "1" // TimeSlotsPicker requires a value for recurrencesPerDay to be displayed
          : undefined),
      doubleStaffing,
      hidden,
      startDate: defaultStartDate
        ? machineFormat(defaultStartDate, "yyyy-MM-dd")
        : undefined,
      endDate: undefined,
    },
  });

  const {
    formState: { errors, isDirty },
    register,
    handleSubmit,
    setError,
    reset,
    getValues,
  } = methods;

  const activityHasPatient = Boolean(patientId);

  // Get category config for conditional rendering (category comes from template)
  const {
    allowsHidden,
    allowsCompetence,
    requiresCompetence,
    allowsInvoicingCode: activityAllowsInvoicingCode,
    allowsDuration,
    allowsDoubleStaffing,
  } = getActivityCategoryConfig({
    category,
    activityHasPatient,
  });

  const canEditInvoicingCodes =
    activityAllowsInvoicingCode &&
    organizationUsesInvoicingCodes(organization) &&
    activityHasPatient;

  const { mutate, isPending } = useMutation({
    mutationFn: async (formData: CreateActivityFromTemplateFormFields) => {
      const newActivity = newActivityFromActivityTemplate({
        formData,
        title,
        category,
        templateId,
        templateRevision,
        patientId,
      });

      await addActivity(newActivity);

      const submittedProcedureCodes = formData.procedureCodes ?? [];
      const submittedProductCodes = formData.productCodes ?? [];

      if (
        shouldSaveInvoice({
          invoicingActivated:
            organizationUsesInvoicingCodes(organization) ?? false,
          category,
          procedureCodes: submittedProcedureCodes,
          productCodes: submittedProductCodes,
          activityHasPatient,
        })
      ) {
        await saveActivityInvoicingCodes({
          activityId: newActivity.activityId,
          patientId,
          procedureCodes: submittedProcedureCodes,
          productCodes: submittedProductCodes,
        });
      }
    },
    onError: (error) => {
      setError("root.server", {
        message: deducedError(error),
      });
      // Reset `isDirty` to support only showing server error when the form is not changed.
      reset(getValues(), {
        keepErrors: true,
        keepIsSubmitted: true,
        keepTouched: true,
        keepIsValid: true,
        keepSubmitCount: true,
      });
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: activityKeys.all }),
        queryClient.invalidateQueries({ queryKey: routeKeys.lists() }),
        queryClient.invalidateQueries({
          queryKey: shiftKeys.listWithContents({}),
        }),
      ]);
      onSuccess?.();
    },
  });

  const validateAndSubmit = handleSubmit((formData) => {
    mutate(formData);
  });

  return (
    <FormProvider {...methods}>
      <Form onSubmit={validateAndSubmit}>
        {errors.root?.server?.message && !isDirty ? (
          <ErrorMessage message={errors.root.server.message} weight="bold" />
        ) : null}

        {/* MEASUREMENTS (for PatientMeasurementTask) */}
        {category === categorySchema.enum.PatientMeasurementTask ? (
          <MeasurementsPicker />
        ) : null}

        {/* DESCRIPTION */}
        <DescriptionInput />

        {/* SCHEDULING (includes DateInput and TimeSlotsPicker) */}
        <Scheduling />

        {/* DURATION */}
        {showDurationInUI && allowsDuration ? <ActivityDuration /> : null}

        {/* COMPETENCE */}
        {allowsCompetence ? (
          <CompetencePicker isRequired={requiresCompetence} />
        ) : null}

        {canEditInvoicingCodes ? (
          <AddInvoicingCodesAction patientId={patientId} />
        ) : null}

        {/* OTHER - Double staffing and Hidden */}
        {allowsDoubleStaffing || allowsHidden ? (
          <Checkboxes legend={t`Övrigt`} orientation="horizontal">
            {allowsDoubleStaffing ? (
              <Checkbox
                errorMessage={errors.doubleStaffing?.message}
                label={{ text: t`Kräver dubbelbemanning` }}
                visualStyle="framed"
                {...register("doubleStaffing")}
              />
            ) : (
              <></>
            )}
            {allowsHidden ? (
              <Checkbox
                errorMessage={errors.hidden?.message}
                label={{ text: t`Dölj aktiviteten för patienten` }}
                visualStyle="framed"
                {...register("hidden")}
              />
            ) : (
              <></>
            )}
          </Checkboxes>
        ) : null}

        <FilledButton type="submit" disabled={isPending}>
          <Trans>Lägg till aktivitet</Trans>
        </FilledButton>
      </Form>
    </FormProvider>
  );
};
